import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Chip } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";

// ─── Config de estados ──────────────────────────────────────────────────────
const STATUS_CONFIG = {
  approved: {
    icon: CheckCircleOutlineIcon,
    color: "#16a34a",
    bg: "#f0fdf4",
    chipBg: "#dcfce7",
    chipColor: "#15803d",
    label: "Pago aprobado",
    headline: "¡Compra exitosa!",
    sub: "Tu pedido fue confirmado. Pronto recibirás un correo con los detalles.",
  },
  pending: {
    icon: HourglassEmptyIcon,
    color: "#d97706",
    bg: "#fffbeb",
    chipBg: "#fef3c7",
    chipColor: "#b45309",
    label: "Pago pendiente",
    headline: "Estamos procesando tu pago",
    sub: "Aún no recibimos la confirmación. Puede demorar unos minutos.",
  },
  error: {
    icon: WarningAmberIcon,
    color: "#dc2626",
    bg: "#fef2f2",
    chipBg: "#fee2e2",
    chipColor: "#b91c1c",
    label: "Error en el pago",
    headline: "No pudimos procesar tu pago",
    sub: "Ocurrió un problema con tu transacción. Por favor intentá de nuevo.",
  },
};

const getConfig = (status) => {
  if (status.includes("✅")) return STATUS_CONFIG.approved;
  if (status.includes("⚠️ El pago aún no fue confirmado")) return STATUS_CONFIG.pending;
  if (status.includes("⚠️") || status.includes("Error") || status.includes("No se encontró"))
    return STATUS_CONFIG.error;
  return STATUS_CONFIG.pending;
};

// ─── Componente principal ───────────────────────────────────────────────────
const BuyOK = () => {
  const [paymentStatus, setPaymentStatus] = useState("⚠️ El pago aún no fue confirmado");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setPaymentStatus("✅ Pago aprobado");
      setIsConfirmed(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const cfg = getConfig(paymentStatus);
  const Icon = cfg.icon;
  const loading = paymentStatus.includes("Verificando");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f9fafb",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: "440px" },
          bgcolor: "#fff",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 20px 40px -8px rgba(0,0,0,0.12)",
        }}
      >
        {/* Banner de color */}
        <Box
          sx={{
            bgcolor: cfg.bg,
            px: { xs: 3, sm: 4 },
            py: { xs: 4, sm: 5 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            borderBottom: `1px solid ${cfg.color}22`,
            transition: "background-color 0.4s ease",
          }}
        >
          {/* Ícono */}
          <Box
            sx={{
              width: { xs: 68, sm: 80 },
              height: { xs: 68, sm: 80 },
              borderRadius: "50%",
              bgcolor: `${cfg.color}18`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
              animation: loading ? "pulse 1.4s ease-in-out infinite" : "none",
              "@keyframes pulse": {
                "0%, 100%": { transform: "scale(1)", opacity: 1 },
                "50%": { transform: "scale(1.07)", opacity: 0.75 },
              },
            }}
          >
            <Icon sx={{ fontSize: { xs: 36, sm: 42 }, color: cfg.color }} />
          </Box>

          {/* Chip de estado */}
          <Chip
            label={cfg.label}
            size="small"
            sx={{
              mb: 1.5,
              bgcolor: cfg.chipBg,
              color: cfg.chipColor,
              fontWeight: 600,
              fontSize: "12px",
              letterSpacing: "0.04em",
              border: "none",
            }}
          />

          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: "#111827", mb: 1, fontSize: { xs: "20px", sm: "22px" } }}
          >
            {cfg.headline}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#6b7280", lineHeight: 1.6, maxWidth: "320px" }}
          >
            {cfg.sub}
          </Typography>
        </Box>

        {/* Cuerpo */}
        <Box
          sx={{
            px: { xs: 3, sm: 4 },
            py: { xs: 3, sm: 3.5 },
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate("/")}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "15px",
              py: 1.3,
              bgcolor: "#111827",
              "&:hover": { bgcolor: "#374151" },
            }}
          >
            Seguir comprando
          </Button>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<ShoppingBagOutlinedIcon />}
            disabled={!isConfirmed}
            onClick={() => navigate("/profile/pedidos")}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              py: 1.2,
              borderColor: "#e5e7eb",
              color: "#374151",
              "&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb" },
              "&.Mui-disabled": { opacity: 0.45 },
            }}
          >
            Ver mis compras
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BuyOK;